/**
 * HeaderVisibilityContext.tsx
 *
 * React Context for controlling MasterLayout header visibility.
 * Allows components to dictate whether the header should be shown or hidden.
 *
 * @example
 * // In a component that needs to hide the header (e.g., HandleChat on mobile)
 * const { setHeaderVisibility } = useHeaderVisibility();
 *
 * useEffect(() => {
 *   setHeaderVisibility(true); // Hide header
 *   return () => setHeaderVisibility(false); // Show on unmount
 * }, []);
 */

import { createContext, useContext, useState, ReactNode, FC } from 'react';

interface HeaderVisibilityContextType {
  /**
   * Whether the header should be hidden
   */
  shouldHideHeader: boolean;

  /**
   * Set header visibility
   * @param hide true to hide header, false to show
   */
  setHeaderVisibility: (hide: boolean) => void;
}

const HeaderVisibilityContext = createContext<HeaderVisibilityContextType | undefined>(undefined);

/**
 * Provider component that manages header visibility state
 */
export const HeaderVisibilityProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [shouldHideHeader, setShouldHideHeader] = useState(false);

  const setHeaderVisibility = (hide: boolean) => {
    setShouldHideHeader(hide);
  };

  return (
    <HeaderVisibilityContext.Provider value={{ shouldHideHeader, setHeaderVisibility }}>
      {children}
    </HeaderVisibilityContext.Provider>
  );
};

/**
 * Hook to access header visibility controls
 * @throws Error if used outside HeaderVisibilityProvider
 */
export const useHeaderVisibility = () => {
  const context = useContext(HeaderVisibilityContext);
  if (!context) {
    throw new Error('useHeaderVisibility must be used within HeaderVisibilityProvider');
  }
  return context;
};
